/**
 * Dashboard-PDF-Import (Lite/Terminal). Liest ein Transportdokument (Bill of Lading, Buchungs-
 * bestätigung einer Reederei, Packliste …) aus und ermittelt daraus die für das Tracking nötigen
 * Angaben: Container-Nummer (ISO 6346), Reederei und — falls im Dokument genannt — das Terminal.
 * Der Nutzer bestätigt/ergänzt die Treffer anschließend im Dialog; gespeichert wird über denselben
 * Lite-Pfad wie beim Excel-Import (`POST /containers/import`, ADR-027).
 *
 * NUR Typen + REINE Logik (kein PDF-Reader) — das Auslesen des Textlayers passiert im `api`-Paket
 * (pdf2json) und reicht hier die Seitentexte herein. So bleibt das Modul dependency-frei, im
 * Frontend als Typquelle nutzbar und ohne PDF-Fixture testbar (analog `excel-import.ts`).
 *
 * Siehe docs/PRD.md §6.6, packages/shared/src/carriers.ts (resolveCarrier/findCarrierInText),
 * packages/shared/src/terminals.ts (matchTerminalByText).
 */
import { isValidContainerNumber, normalizeContainerNumber } from './container-number';
import { findCarrierInText, resolveCarrier, resolveCarrierAlias } from './carriers';
import { matchTerminalByText } from './terminals';
/** Maximale Upload-Größe für PDF-Dokumente (15 MB; Scans/BLs sind größer als Excel-Listen). */
export const PDF_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
/** Erlaubte Dateiendung (Whitelist, SEC-W4). */
export const PDF_ACCEPTED_EXTENSIONS = ['.pdf'];
/**
 * Erlaubte MIME-Typen (SEC-W4). `application/octet-stream` ist tolerant gelistet, weil manche
 * Browser/Mail-Clients PDFs generisch senden — die Endung wird dann zusätzlich geprüft.
 */
export const PDF_ACCEPTED_MIME_TYPES = [
    'application/pdf',
    'application/x-pdf',
    'application/octet-stream',
];
/** Obergrenze der erkannten Zeilen je Dokument (Schutz für UI/Laufzeit). */
export const PDF_IMPORT_MAX_ROWS = 500;
// ── Container-Erkennung im Freitext ───────────────────────────────────────────────────────
/**
 * Container-ähnliche Fundstellen: 4 Buchstaben + 7 Ziffern, wobei vor JEDER Ziffer bis zu zwei
 * Trennzeichen stehen dürfen. PDF-Textlayer zerlegen Nummern beliebig in Text-Runs, sodass beim
 * Zusammensetzen einer Zeile Leerzeichen an unvorhersehbaren Stellen landen („MSKU 123456 5",
 * „MSKU12345 65", „MSKU-1234565"). Ein direkt folgender Ziffernblock schließt den Treffer aus
 * (`(?!\d)`) — sonst würde aus einer COSCO-B/L-Nummer wie „COSU6451762360" fälschlich ein Container.
 */
const CONTAINER_RE = /[A-Z]{4}(?:[\s.-]{0,2}\d){7}(?!\d)/g;
/** ISO-6346-Kategoriekennzeichen (4. Buchstabe) — U = Frachtcontainer, J = Zusatz, Z = Trailer. */
const CATEGORY_LETTERS = new Set(['U', 'J', 'Z']);
/** Zeichen, das einen Treffer nach links begrenzt (kein Teil eines längeren Codes). */
const isWordChar = (ch) => !!ch && /[A-Z0-9]/.test(ch);
/**
 * Findet Container-Nummern in einer Textzeile. Übernommen wird eine Fundstelle nur, wenn entweder
 * die ISO-6346-Prüfziffer stimmt ODER das Präfix formal korrekt ist (4. Buchstabe U/J/Z) — Letzteres
 * fängt Tipp-/OCR-Fehler in echten Nummern ab, ohne beliebige Referenznummern einzusammeln.
 */
export function findContainersInText(line) {
    const text = (line ?? '').toUpperCase();
    const out = [];
    CONTAINER_RE.lastIndex = 0;
    let m;
    while ((m = CONTAINER_RE.exec(text)) !== null) {
        const raw = m[0];
        if (isWordChar(text[m.index - 1]))
            continue; // Teil eines längeren Codes → verwerfen
        const normalized = normalizeContainerNumber(raw);
        const valid = isValidContainerNumber(normalized);
        if (!valid && !CATEGORY_LETTERS.has(normalized.charAt(3)))
            continue;
        out.push({ raw: raw.trim(), containerNumber: normalized, valid });
    }
    return out;
}
// ── Dokumentweite Erkennung ───────────────────────────────────────────────────────────────
const MAX_CONTEXT = 120;
/** Zeile für die Anzeige kürzen (eine Zeile, keine Mehrfach-Leerzeichen). */
function shortContext(line) {
    const clean = (line ?? '').replace(/\s+/g, ' ').trim();
    if (!clean)
        return undefined;
    return clean.length > MAX_CONTEXT ? `${clean.slice(0, MAX_CONTEXT)}…` : clean;
}
/**
 * Sucht das Terminal im Dokument: zeilenweise über `matchTerminalByText` (bewusst konservativ) —
 * es gewinnt das am häufigsten genannte Terminal. `undefined` = kein sicherer Treffer.
 */
export function detectTerminalInLines(lines) {
    const hits = new Map();
    for (const line of lines) {
        const option = matchTerminalByText(line);
        if (!option)
            continue;
        const entry = hits.get(option.id);
        if (entry)
            entry.count += 1;
        else
            hits.set(option.id, { option, count: 1, context: shortContext(line) });
    }
    let best;
    for (const entry of hits.values())
        if (!best || entry.count > best.count)
            best = entry;
    return best ? { option: best.option, context: best.context } : undefined;
}
/** Ermittelt Reederei + Terminal auf Dokumentebene (Fallback für Zeilen ohne eigene Quelle). */
export function detectPdfDocument(pages) {
    const fullText = pages.join('\n');
    const lines = fullText.split(/\r?\n/);
    const carrier = findCarrierInText(fullText);
    const terminal = detectTerminalInLines(lines);
    return {
        ...(carrier ? { carrierCode: carrier.code, carrierName: carrier.name } : {}),
        ...(terminal
            ? {
                terminalId: terminal.option.id,
                terminalLabel: terminal.option.label,
                ...(terminal.context ? { terminalContext: terminal.context } : {}),
            }
            : {}),
        pageCount: pages.length,
        textChars: fullText.replace(/\s/g, '').length,
    };
}
/**
 * Reederei einer Zeile: ZUERST das Container-Präfix (verlässlichste Quelle), dann eine explizit
 * mitgegebene Reederei (KI), dann die dokumentweit erkannte Linie — sonst „none".
 */
export function resolvePdfRowCarrier(containerNumber, detection, explicitCarrierCode) {
    const res = resolveCarrier(containerNumber);
    if (res.status === 'resolved') {
        return { carrierCode: res.carrier.code, carrierName: res.carrier.name, carrierSource: 'prefix' };
    }
    if (explicitCarrierCode) {
        const alias = resolveCarrierAlias(explicitCarrierCode);
        return {
            carrierCode: alias?.code ?? explicitCarrierCode.toUpperCase(),
            ...(alias ? { carrierName: alias.name } : {}),
            carrierSource: 'ai',
        };
    }
    if (detection.carrierCode) {
        return {
            carrierCode: detection.carrierCode,
            ...(detection.carrierName ? { carrierName: detection.carrierName } : {}),
            carrierSource: 'document',
        };
    }
    return { carrierSource: 'none' };
}
/**
 * Baut die Vorschau-Zeilen aus den Seitentexten. Reederei je Zeile: ZUERST über das Container-Präfix
 * (resolveCarrier, am verlässlichsten), sonst die dokumentweit erkannte Reederei, sonst „none"
 * (→ manuell ergänzen oder nur über das Terminal tracken). Terminal je Zeile: Treffer in der eigenen
 * Zeile, sonst das dokumentweit erkannte Terminal. Dedupliziert (2.+ Vorkommen = Dublette).
 */
export function buildPdfImportRows(pages, detection) {
    const rows = [];
    const seen = new Set();
    for (let p = 0; p < pages.length; p++) {
        const lines = (pages[p] ?? '').split(/\r?\n/);
        for (const line of lines) {
            for (const match of findContainersInText(line)) {
                if (rows.length >= PDF_IMPORT_MAX_ROWS)
                    return rows;
                const duplicate = match.valid && seen.has(match.containerNumber);
                if (match.valid)
                    seen.add(match.containerNumber);
                const { carrierCode, carrierName, carrierSource } = resolvePdfRowCarrier(match.containerNumber, detection);
                // Terminal: Treffer in der Fundstellen-Zeile, sonst dokumentweit.
                const lineTerminal = matchTerminalByText(line);
                const terminalId = lineTerminal?.id ?? detection.terminalId;
                const terminalLabel = lineTerminal?.label ?? detection.terminalLabel;
                rows.push({
                    containerNumber: match.containerNumber,
                    raw: match.raw,
                    valid: match.valid,
                    duplicate,
                    page: p + 1,
                    ...(shortContext(line) ? { context: shortContext(line) } : {}),
                    ...(carrierCode ? { carrierCode, carrierName } : {}),
                    carrierSource,
                    ...(terminalId ? { terminalId, terminalLabel } : {}),
                    source: 'deterministic',
                });
            }
        }
    }
    return rows;
}
/** Aggregiert die Vorschau-Kennzahlen (nur verwertbare Zeilen zählen als „mit Reederei/Terminal"). */
export function summarizePdfImport(rows) {
    const usable = rows.filter((r) => r.valid && !r.duplicate);
    return {
        total: rows.length,
        valid: rows.filter((r) => r.valid).length,
        invalid: rows.filter((r) => !r.valid).length,
        duplicates: rows.filter((r) => r.duplicate).length,
        withCarrier: usable.filter((r) => !!r.carrierCode).length,
        withTerminal: usable.filter((r) => !!r.terminalId).length,
        needsCarrier: usable.filter((r) => !r.carrierCode).length,
        trackable: usable.filter((r) => !!r.carrierCode || !!r.terminalId).length,
    };
}
/** Vollständige Vorschau aus Dateiname + Seitentexten (reine Logik, ohne PDF-Reader). */
export function buildPdfImportResult(filename, pages) {
    const detection = detectPdfDocument(pages);
    const rows = buildPdfImportRows(pages, detection);
    return {
        filename,
        detection,
        rows,
        summary: summarizePdfImport(rows),
        aiUsed: false,
        textFound: detection.textChars > 0,
    };
}
//# sourceMappingURL=pdf-import.js.map